import React, { useEffect } from 'react';
import GlobalHeader from '../components/GlobalHeader';
import GlobalFooter from '../components/GlobalFooter';
import LegalHero from '../components/LegalHero';
import PageSEO from '../components/PageSEO';

export default function CookiePolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({
        top: el.offsetTop - 100,
        behavior: 'smooth'
      });
    }
  };

  const navItems = [
    { id: 'sec-1', label: '1. What Are Cookies?' },
    { id: 'sec-2', label: '2. Similar Technologies' },
    { id: 'sec-3', label: '3. How We Use Cookies' },
    { id: 'sec-4', label: '4. Strictly Necessary Cookies' },
    { id: 'sec-5', label: '5. Preferences' },
    { id: 'sec-6', label: '6. Analytics and Performance' },
    { id: 'sec-7', label: '7. Authentication' },
    { id: 'sec-8', label: '8. Payments' },
    { id: 'sec-9', label: '9. Third-Party Technologies' },
    { id: 'sec-10', label: '10. Advertising and Tracking' },
    { id: 'sec-11', label: '11. Cookie Consent' },
    { id: 'sec-12', label: '12. Changing Your Preferences' },
    { id: 'sec-13', label: '13. Cookie Duration' },
    { id: 'sec-14', label: '14. Preference Records' },
    { id: 'sec-15', label: '15. Browser Privacy Signals' },
    { id: 'sec-16', label: '16. Policy Changes' },
    { id: 'sec-17', label: '17. Privacy Policy' },
    { id: 'sec-18', label: '18. Contact' },
  ];

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://aisearchadvisers.com/#organization",
        "name": "AI Search Advisers",
        "url": "https://aisearchadvisers.com/",
        "description": "AI Search Advisers helps businesses assess and improve website readiness for AI-driven discovery through website audits, AI visibility insights, crawlability analysis, structured data evaluation, and prioritized recommendations.",
        "email": "info@aisearchadvisers.com"
      },
      {
        "@type": "WebSite",
        "@id": "https://aisearchadvisers.com/#website",
        "url": "https://aisearchadvisers.com/",
        "name": "AI Search Advisers",
        "publisher": { "@id": "https://aisearchadvisers.com/#organization" },
        "inLanguage": "en-US"
      },
      {
        "@type": "WebPage",
        "@id": "https://aisearchadvisers.com/cookie-policy#webpage",
        "url": "https://aisearchadvisers.com/cookie-policy",
        "name": "Cookie Policy | AI Search Advisers",
        "description": "Learn how AI Search Advisers uses cookies and similar technologies and understand the options available for managing cookie preferences.",
        "isPartOf": { "@id": "https://aisearchadvisers.com/#website" },
        "about": { "@id": "https://aisearchadvisers.com/#organization" },
        "publisher": { "@id": "https://aisearchadvisers.com/#organization" },
        "inLanguage": "en-US"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] font-sans text-[#021426] flex flex-col selection:bg-[#FEAB3D]/30 selection:text-[#021426]">
      <PageSEO 
        title="Cookie Policy | AI Search Advisers"
        description="Learn how AI Search Advisers uses cookies and similar technologies and understand the options available for managing cookie preferences."
        canonical="https://aisearchadvisers.com/cookie-policy"
        schema={schema}
      />
      <GlobalHeader />

      <LegalHero 
        eyebrow="LEGAL"
        title="Cookie Policy"
        supportingText="AI Search Advisers"
        effectiveDate="September 21, 2026"
        lastUpdated="September 21, 2026"
      />

      {/* CONTENT & SIDEBAR */}
      <section className="px-6 py-16 relative z-20">
        <div className="max-w-[1100px] mx-auto flex flex-col lg:flex-row gap-12 lg:gap-20 items-start">
          
          {/* SIDEBAR NAVIGATION (Desktop) */}
          <div className="hidden lg:block w-64 shrink-0 sticky top-32">
            <h4 className="font-['Fraunces',serif] text-lg font-bold text-[#021426] mb-4">On this page</h4>
            <div className="flex flex-col gap-3 border-l-2 border-[#e5e9ee] pl-4 max-h-[70vh] overflow-y-auto pr-2 pb-10">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-left text-sm text-[#4C545B] hover:text-[#0C5B94] hover:translate-x-1 transition-all font-medium"
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {/* MAIN CONTENT */}
          <div className="flex-1 max-w-[800px] legal-content w-full">
            <p>This Cookie Policy explains how AI Search Advisers uses cookies and similar technologies when you visit <a href="https://aisearchadvisers.com/">https://aisearchadvisers.com/</a> or use our website and related services.</p>
            <p>This policy should be read together with our <a href="/privacy-policy">Privacy Policy</a>, which explains more broadly how we collect, use, and protect personal information.</p>
            <p>If you have questions about this Cookie Policy or our privacy practices, contact us at <a href="mailto:info@aisearchadvisers.com">info@aisearchadvisers.com</a>.</p>
            
            <h2 id="sec-1">1. What Are Cookies?</h2>
            <p>Cookies are small text files that websites can place on your browser or device.</p>
            <p>They are commonly used to:</p>
            <ul>
              <li>Keep websites functioning properly.</li>
              <li>Maintain secure sessions.</li>
              <li>Remember user preferences.</li>
              <li>Support account authentication.</li>
              <li>Remember privacy or cookie choices.</li>
              <li>Understand how a website or feature is being used.</li>
              <li>Support integrations with third-party services.</li>
            </ul>
            <p>Cookies may be temporary and expire when you close your browser, or they may remain on your device for a defined period.</p>
            
            <h2 id="sec-2">2. Similar Technologies</h2>
            <p>When we refer to "cookies" in this policy, we may also be referring to similar technologies that store information on, or access information from, your browser or device.</p>
            <p>These can include:</p>
            <ul>
              <li>Local storage</li>
              <li>Session storage</li>
              <li>Pixels</li>
              <li>Tags</li>
              <li>Scripts</li>
              <li>Software development tools</li>
              <li>Similar browser or device technologies</li>
            </ul>
            <p>The technology used may vary depending on the feature or service involved.</p>
            
            <h2 id="sec-3">3. How AI Search Advisers Uses Cookies</h2>
            <p>AI Search Advisers uses, or may use, cookies and similar technologies where reasonably necessary to operate, secure, and improve the website and Service.</p>
            <p>Our core analytics offering is designed to operate without placing analytics cookies on the visitors of customer websites.</p>
            <p>However, the AI Search Advisers website itself may use limited storage technologies for functions such as account access, security, preferences, payment-related workflows, and remembering privacy choices.</p>
            <p>We categorize these technologies based on their purpose.</p>
            
            <h2 id="sec-4">4. Strictly Necessary Cookies</h2>
            <p>Strictly necessary cookies and similar technologies support essential functions of the website and Service.</p>
            <p>They may be used for purposes such as:</p>
            <ul>
              <li>Maintaining secure user sessions.</li>
              <li>Authenticating signed-in users.</li>
              <li>Protecting accounts and forms against abuse.</li>
              <li>Preventing fraud or malicious activity.</li>
              <li>Supporting website security.</li>
              <li>Maintaining server or application functionality.</li>
              <li>Remembering your cookie or privacy preferences.</li>
              <li>Supporting requested checkout or payment functionality.</li>
            </ul>
            <p>Because these technologies are required to provide functionality you request or to keep the Service secure, they may not always be subject to optional cookie controls.</p>
            <p>Disabling these technologies through your browser may prevent parts of the Service from working correctly.</p>
            
            <h2 id="sec-5">5. Preferences</h2>
            <p>Where used, preference technologies help us remember choices you make when using the website.</p>
            <p>These may include preferences such as:</p>
            <ul>
              <li>Display settings</li>
              <li>Interface preferences</li>
              <li>Previously selected options</li>
              <li>Privacy and consent choices</li>
            </ul>
            <p>These technologies are intended to improve usability and are not used to make significant decisions about you.</p>
            <p>Where required by applicable law, optional preference technologies will only be used after you have provided the appropriate consent.</p>
            
            <h2 id="sec-6">6. Analytics and Performance</h2>
            <p>AI Search Advisers is designed around privacy-conscious analytics.</p>
            <p>Our core website analytics technology is intended to operate without relying on cookies to identify or follow individuals across unrelated websites.</p>
            <p>Where we use additional analytics or performance technologies on aisearchadvisers.com, they may help us understand information such as:</p>
            <ul>
              <li>Which pages are visited.</li>
              <li>How visitors navigate the website.</li>
              <li>Whether website features operate correctly.</li>
              <li>General usage patterns.</li>
              <li>Technical errors and performance issues.</li>
              <li>Referring websites or sources.</li>
            </ul>
            <p>Where an analytics technology requires access to or storage of information on your device and applicable law requires consent, we will ask for that consent before enabling the technology.</p>
            <p>We do not intend to use analytics technologies to create cross-site behavioral profiles of visitors.</p>
            
            <h2 id="sec-7">7. Authentication</h2>
            <p>If you create or sign into an AI Search Advisers account, cookies or similar storage technologies may be required to:</p>
            <ul>
              <li>Keep you securely signed in.</li>
              <li>Verify your identity.</li>
              <li>Protect your account.</li>
              <li>Maintain your session.</li>
              <li>Detect suspicious activity.</li>
              <li>Remember certain account-related settings.</li>
            </ul>
            <p>These technologies are generally necessary for requested account functionality.</p>
            <p>Logging out may remove or invalidate some authentication-related information.</p>
            
            <h2 id="sec-8">8. Payments</h2>
            <p>When you purchase or manage a paid AI Search Advisers service, our payment provider may use cookies or similar technologies as part of:</p>
            <ul>
              <li>Secure checkout.</li>
              <li>Payment processing.</li>
              <li>Fraud prevention.</li>
              <li>Subscription management.</li>
              <li>Transaction security.</li>
            </ul>
            <p>Payment providers operate their own systems and may process information according to their own privacy and cookie policies.</p>
            <p>AI Search Advisers does not directly store complete payment card details.</p>
            
            <h2 id="sec-9">9. Third-Party Technologies</h2>
            <p>Some features of our website may rely on third-party service providers.</p>
            <p>Depending on the features you use, these providers may include services relating to:</p>
            <ul>
              <li>Hosting and infrastructure</li>
              <li>Authentication</li>
              <li>Payment processing</li>
              <li>Email delivery</li>
              <li>Customer support</li>
              <li>Error monitoring</li>
              <li>Security</li>
              <li>AI and search functionality</li>
            </ul>
            <p>A third party may use its own cookies or similar technologies when its service is activated.</p>
            <p>Where applicable, we take reasonable steps to ensure that optional third-party technologies are not activated until any legally required consent has been obtained.</p>
            <p>Third-party providers are responsible for their own privacy practices.</p>
            
            <h2 id="sec-10">10. Advertising and Tracking</h2>
            <p>AI Search Advisers does not use cookies for the purpose of selling personal information.</p>
            <p>We do not use cookies to intentionally create cross-site behavioral profiles for unrelated third-party advertising.</p>
            <p>If our use of advertising or tracking technologies materially changes in the future, we will update this Cookie Policy and, where required, request appropriate consent before activating those technologies.</p>
            
            <h2 id="sec-11">11. Cookie Consent</h2>
            <p>Where applicable law requires consent for optional cookies or similar technologies, AI Search Advisers provides controls that allow you to make a choice.</p>
            <p>Depending on the technologies currently enabled on the website, you may be able to:</p>
            <ul>
              <li>Accept optional technologies.</li>
              <li>Reject optional technologies.</li>
              <li>Select specific categories.</li>
              <li>Change your preferences later.</li>
            </ul>
            <p>Strictly necessary technologies may remain active because they are required to provide requested functionality or maintain website security.</p>
            <p>Rejecting optional cookies should not prevent you from accessing the core public areas of the website.</p>
            
            <h2 id="sec-12">12. Changing Your Preferences</h2>
            <p>Where a cookie preference control is available, you can change your selection through the website's Cookie Settings or privacy control.</p>
            <p>Your latest preference will replace your previous choice where technically possible.</p>
            <p>You may also manage cookies directly through your browser.</p>
            <p>Most modern browsers allow you to:</p>
            <ul>
              <li>View stored cookies.</li>
              <li>Delete cookies.</li>
              <li>Block certain cookies.</li>
              <li>Block third-party cookies.</li>
              <li>Clear browsing data.</li>
              <li>Restrict website storage.</li>
            </ul>
            <p>Please note that blocking all cookies or storage technologies may affect website functionality, including login and account features.</p>
            
            <h2 id="sec-13">13. Cookie Duration</h2>
            <p>The length of time a cookie or similar technology remains available depends on its purpose.</p>
            
            <h3>Session technologies</h3>
            <p>Some information exists only while your browser session is active and is removed or invalidated when the session ends.</p>
            
            <h3>Persistent technologies</h3>
            <p>Other technologies may remain for a defined period in order to remember settings, security information, or privacy preferences.</p>
            <p>We aim to retain cookie and device-storage information only for as long as reasonably necessary for its intended purpose.</p>
            
            <h2 id="sec-14">14. Preference Records</h2>
            <p>Where we request cookie consent, we may store a record of your preference so that we do not repeatedly ask you to make the same selection on every page visit.</p>
            <p>This preference record may itself use a strictly necessary cookie or similar storage technology.</p>
            
            <h2 id="sec-15">15. Browser Privacy Signals</h2>
            <p>Browsers and devices may provide privacy controls or signals relating to tracking.</p>
            <p>Where required by applicable law and technically applicable to our Service, we will respect recognized privacy choices.</p>
            <p>Because standards and browser implementations may change over time, the behavior of individual browser privacy controls may vary.</p>
            
            <h2 id="sec-16">16. Policy Changes</h2>
            <p>We may update this Cookie Policy when:</p>
            <ul>
              <li>Our website technology changes.</li>
              <li>New services or integrations are introduced.</li>
              <li>Our use of cookies changes.</li>
              <li>Applicable legal requirements change.</li>
            </ul>
            <p>When we update this policy, we will revise the "Last updated" date at the top of this page.</p>
            <p>If a change materially affects how optional cookies or similar technologies are used, we may request your consent again where required.</p>
            
            <h2 id="sec-17">17. Privacy Policy</h2>
            <p>For more information about how AI Search Advisers collects, uses, shares, retains, and protects personal information, please review our:</p>
            <p><strong>Privacy Policy:</strong> <a href="/privacy-policy">/privacy-policy</a></p>
            
            <h2 id="sec-18">18. Contact</h2>
            <p>If you have questions about our use of cookies or similar technologies, contact:</p>
            <p><strong>AI Search Advisers</strong><br/>
               Website: <a href="https://aisearchadvisers.com/">https://aisearchadvisers.com/</a><br/>
               Email: <a href="mailto:info@aisearchadvisers.com">info@aisearchadvisers.com</a>
            </p>

            <div className="mt-16 pt-8 border-t border-[#e5e9ee]">
              <h2 id="technologies" className="font-['Fraunces',serif] text-2xl font-bold text-[#021426] mb-6">Cookies and Technologies Currently Used</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm border-collapse min-w-[600px]">
                  <thead>
                    <tr className="border-b border-[#e5e9ee] bg-[#FAF7F2]">
                      <th className="p-3 font-semibold text-[#021426]">Name</th>
                      <th className="p-3 font-semibold text-[#021426]">Provider</th>
                      <th className="p-3 font-semibold text-[#021426]">Purpose</th>
                      <th className="p-3 font-semibold text-[#021426]">Category</th>
                      <th className="p-3 font-semibold text-[#021426]">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-[#e5e9ee]">
                      <td className="p-3 text-[#4C545B] font-mono text-xs">token</td>
                      <td className="p-3 text-[#4C545B]">AI Search Advisers</td>
                      <td className="p-3 text-[#4C545B]">Stores secure authentication token for signed-in users.</td>
                      <td className="p-3 text-[#4C545B]"><span className="bg-[#e5e9ee] text-[#021426] px-2 py-1 rounded text-xs font-semibold">Strictly Necessary</span></td>
                      <td className="p-3 text-[#4C545B]">Persistent (cleared on logout)</td>
                    </tr>
                    <tr className="border-b border-[#e5e9ee]">
                      <td className="p-3 text-[#4C545B] font-mono text-xs">user</td>
                      <td className="p-3 text-[#4C545B]">AI Search Advisers</td>
                      <td className="p-3 text-[#4C545B]">Stores necessary profile metadata for the authenticated session.</td>
                      <td className="p-3 text-[#4C545B]"><span className="bg-[#e5e9ee] text-[#021426] px-2 py-1 rounded text-xs font-semibold">Strictly Necessary</span></td>
                      <td className="p-3 text-[#4C545B]">Persistent (cleared on logout)</td>
                    </tr>
                    <tr className="border-b border-[#e5e9ee]">
                      <td className="p-3 text-[#4C545B] font-mono text-xs">cookie_consent, cookie_analytics</td>
                      <td className="p-3 text-[#4C545B]">AI Search Advisers</td>
                      <td className="p-3 text-[#4C545B]">Remembers your privacy and cookie consent preferences.</td>
                      <td className="p-3 text-[#4C545B]"><span className="bg-[#e5e9ee] text-[#021426] px-2 py-1 rounded text-xs font-semibold">Strictly Necessary</span></td>
                      <td className="p-3 text-[#4C545B]">Persistent</td>
                    </tr>
                    <tr className="border-b border-[#e5e9ee]">
                      <td className="p-3 text-[#4C545B] font-mono text-xs">PostHog</td>
                      <td className="p-3 text-[#4C545B]">PostHog</td>
                      <td className="p-3 text-[#4C545B]">Anonymous analytics to understand website usage and technical performance. (Loaded only with consent).</td>
                      <td className="p-3 text-[#4C545B]"><span className="bg-[#FEAB3D]/20 text-[#021426] px-2 py-1 rounded text-xs font-semibold">Analytics</span></td>
                      <td className="p-3 text-[#4C545B]">Up to 1 year</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        </div>
      </section>

      <GlobalFooter />
    </div>
  );
}
